import React from 'react';
import { 
  Box, 
  Card, 
  Heading, 
  Image, 
  Stack, 
  Text, 
  HStack, 
  Badge, 
  Icon, 
  Flex,
  useColorModeValue,
  Button
} from '@chakra-ui/react';
import { FaRupeeSign, FaCalendarAlt, FaPaw } from 'react-icons/fa';

export default function AnimalCardProfile({ animal, onEdit, onDelete }) {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const highlightColor = useColorModeValue('green.500', 'green.300');
  
  // Calculate days ago
  const createdAt = new Date(animal.createdAt);
  const now = new Date();
  const timeDiff = now.getTime() - createdAt.getTime();
  const daysAgo = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
  
  // Get first image
  const firstImage = animal.images && animal.images.length > 0 
    ? animal.images[0] 
    : animal.udderPhotoUrl || '/placeholder-image.jpg';
  
  return (
    <Card 
      direction={{ base: 'column', sm: 'row' }}
      overflow='hidden'
      variant='outline'
      bg={bgColor}
      borderColor={borderColor}
      borderRadius="lg"
      transition="all 0.3s"
      _hover={{ 
        transform: 'translateY(-4px)', 
        boxShadow: 'md',
        borderColor: highlightColor
      }}
      h="100%"
    >
      <Image
        objectFit='cover'
        maxW={{ base: '100%', sm: '140px' }}
        src={firstImage}
        alt={animal.title}
        fallbackSrc="https://via.placeholder.com/140x140?text=Animal+Image"
      />

      <Stack flex={1} p={4}>
        <Box mb={2}>
          <HStack mb={1}>
            <Badge colorScheme="green" fontSize="xs">
              <Icon as={FaPaw} mr={1} />
              {animal.animalType}
            </Badge>
            {animal.lactationCycle && (
              <Badge 
                colorScheme={
                  animal.lactationCycle === 'not_calved' ? 'red' : 
                  animal.lactationCycle === 'first' ? 'blue' : 
                  animal.lactationCycle === 'second' ? 'purple' : 'gray'
                }
              >
                {animal.lactationCycle === 'not_calved' ? 'Not Calved' :
                 animal.lactationCycle === 'first' ? 'First Lactation' :
                 animal.lactationCycle === 'second' ? 'Second Lactation' :
                 'Other Lactation'}
              </Badge>
            )}
          </HStack>
          
          <Heading size='md' noOfLines={1}>{animal.title}</Heading>
          
          <Text py={1} color="gray.600" fontSize="sm" noOfLines={2}>
            {animal.description}
          </Text>
        </Box>

        <Flex mt="auto" justify="space-between" align="center">
          <HStack>
            <Icon as={FaRupeeSign} color={highlightColor} />
            <Text fontWeight="bold" fontSize="lg" color={highlightColor}>
              {animal.price}
            </Text>
          </HStack>
          
          <HStack spacing={1}>
            <Icon as={FaCalendarAlt} color="gray.500" fontSize="xs" />
            <Text fontSize="xs" color="gray.500">
              {daysAgo === 0 ? 'Today' : daysAgo === 1 ? 'Yesterday' : `${daysAgo} days ago`}
            </Text>
          </HStack>
        </Flex>
        
        {animal.currentMilkPerDay && (
          <Text fontSize="sm" color="gray.500">
            Milk: {animal.currentMilkPerDay} L/day
          </Text>
        )}
      </Stack>
      
      {/* Action Buttons */}
      <Stack direction="row" p={4} spacing={2}>
        <Button
          size="sm"
          colorScheme="blue"
          onClick={() => onEdit(animal)}
        >
          Edit
        </Button>
        <Button
          size="sm"
          colorScheme="red"
          onClick={() => onDelete(animal._id)}
        >
          Delete
        </Button>
      </Stack>
    </Card>
  );
}